/*
 * The release-note list: /releasenotes (S42, 2026-10-12).
 *
 * The owner's reference is the accoTOOL feedback site's changelog: every entry in
 * FULL, newest first, each with its date to the left, a row of labels (product,
 * version), the title as a heading that links to the entry's own page, and then
 * the whole body. So this is not a list of cards with excerpts like the browse
 * page - a reader scrolls through the notes themselves, and the title link
 * (releaseNoteHref) is for sharing or bookmarking one. The order is the server's:
 * publish date, newest first, which is also the owner's answer.
 *
 * The frame is the browse page's, so the two read as one site: the same sidebar
 * with the same PRODUCT box (ProductFilter.tsx, the owner's "re-use the PRODUCT
 * filter box"), the same `?products=1,2` in the URL, a Clear button, and the
 * "Signed in as" block at its foot. The box's options come from THIS response's
 * own facet - products with live release notes, counted by release notes - never
 * from the bootstrap, which counts live ARTICLES and would hide a product that
 * only has release notes. The facet is not narrowed by the filter, so a reader
 * can widen a selection as well as narrow it.
 *
 * Search (S43, 2026-10-13): the owner asked for release-note search "similar to
 * articles search", and the sidebar gained the browse page's search card above
 * the PRODUCT box - the same component (SearchFilter.tsx) with the same
 * behaviour: `?q=` written 350 ms after the last keystroke, the page reset by a
 * new search, an address changed from outside (Back, a link) adopted into the
 * box. The search ONLY narrows the list: the order stays newest publish date
 * first, even where the server's full-text index could rank by relevance - the
 * owner's answer the same day - which is also why there is no sort control. The
 * facet is not narrowed by the search either, as on the browse page. What the
 * search found is said in the count line and the empty state, from the server's
 * echo of it (trimmed, null when blank), and "Clear N filters" counts it and
 * clears it with the products. (In S42 the owner had answered "no site search"
 * for release notes - that was about release notes appearing in the ARTICLE
 * search, which they still do not; this is search within the release notes.)
 *
 * Paging is the existing Pagination, ten whole notes a page, `?page=` in the URL.
 * A page of full bodies is long, and Next sits at its foot, so a page change also
 * moves the reader - focus and scroll - to the top of the list: left where it was,
 * the reader would land at the bottom of the next page with nothing to say so.
 *
 * Headings: the page's outline stays whole with any number of notes on it. The
 * regions open with visually hidden h2s, as the browse page's do (W11); each
 * note's title is an h3 under the list's; and a note's own headings are shifted
 * down two levels (`##` renders as h4) so they nest under its title rather than
 * outranking it. Every note's heading ids carry a per-note prefix, which is also
 * the id of the note's <article>, because two notes with a "Fixes" section would
 * otherwise put the same id on the page twice.
 *
 * Each note renders through its own small component so that its image-src
 * builder can be memoised per note: Markdown re-parses a whole body whenever that
 * function's identity changes, and one fresh arrow per note per render would
 * re-parse all ten bodies every time the page re-renders for any reason.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import {
  publicReleaseNoteImagePath,
  releaseNoteHref,
  type PublicReleaseNote,
} from '@accotool-kb/shared';
import { listReleaseNotes } from '../api/publicApi.js';
import { forReader } from '../api/publicReader.js';
import { usePublicReader } from '../auth/AuthProvider.js';
import { useAsync } from '../hooks/useAsync.js';
import { DateTime, EmptyState, ErrorBanner, Loading, Pagination, Tag } from '../components/ui.js';
import { Markdown } from '../components/Markdown.js';
import { usePublicContext } from './PublicLayout.js';
import { ProductFilter } from './ProductFilter.js';
import { SearchFilter, useUrlSearch } from './SearchFilter.js';
import { parseIds, parsePage } from './urlState.js';
import { SignedInAs } from '../site/SignedInAs.js';
import { listPageTabTitle, releaseNotesHeading } from './pageHeadings.js';
import { useLanguage } from '../i18n/language.js';

/**
 * The id a note's <article> carries on the list - `rn-accoplanning-v1-0-87-3` -
 * and, with a trailing hyphen, the prefix of every heading id inside it. Both
 * halves of the address are in it because versions repeat across products, and
 * the dots become hyphens rather than vanishing: the shared heading-id rule drops
 * dots, which would make v1.23.4.5 and v12.3.4.5 the same id.
 */
export function releaseNoteAnchorId(productSlug: string, version: string): string {
  return `rn-${productSlug}-${version.replace(/\./g, '-')}`;
}

/** /releasenotes: every live release note in full, newest first, filtered by product and search. */
export function ReleaseNotesPage(): JSX.Element {
  const { bootstrap } = usePublicContext();
  const { language } = useLanguage();
  const t = language.messages;
  const heading = releaseNotesHeading(bootstrap.site, language.code);
  const [searchParams, setSearchParams] = useSearchParams();

  const products = parseIds(searchParams.get('products'));
  const page = parsePage(searchParams.get('page'));

  const update = (changes: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (resetPage) next.delete('page');
    setSearchParams(next);
  };

  // The browse page's search box, rules and all: useUrlSearch.
  const { draft: searchDraft, setDraft: setSearchDraft, search } = useUrlSearch(
    searchParams.get('q') ?? '',
    (settled) => update({ q: settled || null }),
  );

  // Keyed on the joined ids rather than the array, which is new on every render.
  const reader = usePublicReader();
  const state = useAsync(
    // As the reader (S46): a note's link to a private article works for its
    // audiences and is plain text for everybody else.
    (signal) => forReader(reader, () => listReleaseNotes({ products, q: search, page }, signal, reader)),
    [products.join(','), search, page, reader.ready, reader.key],
  );
  const data = state.data;

  /*
   * The tab's title, restored to the bare site title on the way out so the next
   * page does not inherit it. Set again once the first page of notes has
   * arrived: on a fresh load the branding provider stamps the bare site title in
   * the commit after this page mounts (it learns the title from the bootstrap
   * the same moment this page appears), and a title set only on mount would be
   * overwritten by it.
   */
  const loaded = data !== null;
  useEffect(() => {
    document.title = listPageTabTitle(heading.title, bootstrap.site.title);
    return () => {
      document.title = bootstrap.site.title;
    };
  }, [heading.title, bootstrap.site.title, loaded]);

  // A link to one note on the list (#rn-...) arrives before the notes do, so the
  // browser cannot scroll to it on its own - the same reason ArticlePage does this.
  useEffect(() => {
    if (!loaded || !window.location.hash) return;
    let id: string;
    try {
      id = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return; // A malformed escape names nothing on this page.
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loaded]);

  const resultsHeading = useRef<HTMLHeadingElement>(null);

  /*
   * To the top of the list, both ways a reader can be there: focus on the list's
   * heading, so a screen reader starts the new page from its start, and scroll,
   * so a sighted reader does. The heading is visually hidden but sits where the
   * list begins. The sticky header would cover it, so the scroll stops short by
   * the header's height as it is right now - which depends on how many rows it
   * wrapped to at this width.
   */
  const goToPage = (nextPage: number) => {
    update({ page: nextPage > 1 ? String(nextPage) : null }, false);
    const heading = resultsHeading.current;
    if (!heading) return;
    const header = document.querySelector('.site-header');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    heading.style.scrollMarginTop = `${Math.ceil(headerHeight) + 16}px`;
    heading.focus({ preventScroll: true });
    heading.scrollIntoView?.({ block: 'start' });
  };

  const facets = data?.products ?? [];
  // The search counts as a filter here, and Clear clears it too: on this page it
  // is the one other thing narrowing the list. Blank is no search - the server
  // reads it that way as well.
  const activeFilterCount = products.length + (search.trim() !== '' ? 1 : 0);
  // Layout decisions read the server's echo of the filter, not the address bar:
  // the server has dropped junk ids and duplicates, trimmed the search, and the
  // echo describes the page actually on screen rather than the one still being
  // fetched (the count line would otherwise name the new words over the old list).
  const echoed = data?.filter;

  return (
    <>
      <div className="page-header">
        <div>
          {/* Both from the Settings page since 2026-10-15; an empty subtitle is none. */}
          <h1>{heading.title}</h1>
          {heading.subtitle && <p>{heading.subtitle}</p>}
        </div>
      </div>

      <div className="browse-layout">
        <aside className="sidebar" aria-labelledby="release-note-filters-heading">
          <h2 id="release-note-filters-heading" className="visually-hidden">
            {t.filters.heading}
          </h2>

          <SearchFilter
            label={t.releaseNotes.searchLabel}
            value={searchDraft}
            onChange={setSearchDraft}
          />

          {facets.length > 0 && (
            <ProductFilter
              selected={products}
              onChange={(next) => update({ products: next.join(',') || null })}
              items={facets.map((facet) => ({
                id: facet.productId,
                label: facet.name,
                count: facet.count,
              }))}
            />
          )}

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="button button-quiet"
              onClick={() => update({ products: null, q: null })}
            >
              {t.filters.clear(activeFilterCount)}
            </button>
          )}

          <SignedInAs />
        </aside>

        <section className="release-notes-results" aria-labelledby="release-notes-heading">
          <h2
            id="release-notes-heading"
            className="visually-hidden"
            tabIndex={-1}
            ref={resultsHeading}
          >
            {t.releaseNotes.heading}
          </h2>

          <div className="toolbar">
            <p className="muted toolbar-grow" style={{ margin: 0 }} aria-live="polite">
              {/* How many notes, and - when the server says the list was searched -
                  what for: "2 release notes match “locking”" (the dictionary's
                  count, since S48). Given the ECHOED search, so it names the words
                  the list on screen was found by, trimmed as the server read them,
                  rather than whatever the box holds while the next page of results
                  is on its way. */}
              {data ? t.releaseNotes.count(data.total, data.filter.search) : ' '}
            </p>
          </div>

          {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}

          {state.loading && !data && <Loading label={t.releaseNotes.finding} />}

          {data && data.items.length === 0 && (
            <EmptyState
              title={data.total > 0 ? t.releaseNotes.nothingOnThisPage : t.releaseNotes.nothingMatches}
            >
              {data.total > 0 ? (
                <>
                  <p>{t.releaseNotes.fewerPages}</p>
                  <p>
                    <button
                      type="button"
                      className="button"
                      onClick={() => update({ page: null }, false)}
                    >
                      {t.releaseNotes.firstPage}
                    </button>
                  </p>
                </>
              ) : (
                <p>
                  {echoed?.search
                    ? t.releaseNotes.noMatch(echoed.search, echoed.productIds.length > 0)
                    : echoed && echoed.productIds.length > 0
                      ? t.releaseNotes.noneForProducts
                      : t.releaseNotes.noneYet}
                </p>
              )}
            </EmptyState>
          )}

          {data && data.items.length > 0 && (
            <>
              <ol className="release-note-list">
                {data.items.map((note) => (
                  <li key={note.releaseNoteId}>
                    <ReleaseNoteEntry note={note} />
                  </li>
                ))}
              </ol>

              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onChange={goToPage}
              />
            </>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * One note on the list, in the changelog's shape: the date in its own column,
 * then the labels, the title linking to the note's own page, and the whole body.
 */
function ReleaseNoteEntry({ note }: { note: PublicReleaseNote }): JSX.Element {
  const productSlug = note.product.slug;
  const { version } = note;
  const anchor = releaseNoteAnchorId(productSlug, version);

  // Built from the note's own address, as the server sent it, and memoised per
  // note: see the header.
  const buildImageSrc = useCallback(
    (fileName: string) => publicReleaseNoteImagePath(productSlug, version, fileName),
    [productSlug, version],
  );

  return (
    <article className="release-note" id={anchor}>
      <div className="release-note-date">
        <DateTime value={note.publishAtUtc} dateOnly />
      </div>

      <div className="release-note-main">
        <div className="tag-list release-note-labels">
          <Tag kind="product">{note.product.name}</Tag>
          <Tag>{version}</Tag>
        </div>

        <h3 className="release-note-title">
          <Link to={releaseNoteHref(productSlug, version)}>{note.title}</Link>
        </h3>

        <Markdown
          content={note.content}
          linkTargets={note.linkTargets}
          buildImageSrc={buildImageSrc}
          videos={false}
          headingOffset={2}
          headingIdPrefix={`${anchor}-`}
        />
      </div>
    </article>
  );
}
